import React, { useMemo, useState } from 'react';
import { MultiSelect } from '@mantine/core';
import type { MultiSelectProps } from '@mantine/core';
import { getUniqueTagNames, normalizeTagName } from '../../Utils/TagUtils';

export type TagsInputProps = Omit<
	MultiSelectProps,
	'data' | 'value' | 'onChange' | 'searchValue' | 'onSearchChange'
> & {
	availableTags: string[];
	value?: string[];
	onChange?: (value: string[]) => void;
};

export default function TagsInput(props: TagsInputProps) {
	const { value = [], onChange, availableTags, disabled, ...rest } = props;

	const [searchValue, setSearchValue] = useState('');

	const selected = useMemo(() => getUniqueTagNames(value), [value]);
	const selectedKeys = useMemo(() => new Set(selected.map((t) => t.toLowerCase())), [selected]);

	const normalizedAvailableTags = useMemo(() => {
		return getUniqueTagNames(availableTags);
	}, [availableTags]);

	const availableOptions = useMemo(() => {
		return normalizedAvailableTags
			.filter((t) => !selectedKeys.has(t.toLowerCase()))
			.map((t) => ({ value: t, label: t }));
	}, [normalizedAvailableTags, selectedKeys]);

	const allOptions = useMemo(() => {
		const selectedMissing = selected
			.filter((val) => !normalizedAvailableTags.some((t) => t.toLowerCase() === val.toLowerCase()))
			.map((val) => ({ value: val, label: val }));

		const normalizedSearch = normalizeTagName(searchValue);

		const canCreate =
			normalizedSearch.length > 0 &&
			!selectedKeys.has(normalizedSearch.toLowerCase()) &&
			!normalizedAvailableTags.some((t) => t.toLowerCase() === normalizedSearch.toLowerCase());

		const createOption = canCreate ? [{ value: normalizedSearch, label: `+ Create "${normalizedSearch}"` }] : [];

		return [...availableOptions, ...selectedMissing, ...createOption];
	}, [availableOptions, selected, normalizedAvailableTags, searchValue, selectedKeys]);

	const handleChange = (newValue: string[]) => {
		onChange?.(getUniqueTagNames(newValue));
	};

	return (
		<MultiSelect
			{...rest}
			data={allOptions}
			value={selected}
			onChange={handleChange}
			searchValue={searchValue}
			onSearchChange={setSearchValue}
			disabled={disabled}
			searchable
			clearable
		/>
	);
}
